import React, { useState, useEffect, useRef } from "react";
import { Table } from "react-bootstrap";
import "./TableDetails.scss";
import TableHeadItem from "./TableHeadItem";
import TableItem from "./TableIDatatem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import Pagination from "../pagination/Pagination";
import FilterSelect from "../Filter/FilterSelect";
import { tab } from "@testing-library/user-event/dist/tab";
import Loader from "../Loader/Loader";
import { useDragScroll } from "../hooks/useDragScroll";
import DataNotFound from "../DataNotFound/DataNotFound";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";

const options = [10, 15, 20, 25, 50];
const pageSizeOptions = options.map((opt) => ({
  label: opt,
  value: opt,
}));

const TableDetailsInner = ({
  tableData,
  headers,
  totalRecords,
  headerClassification = false,
  showPagination = false,
  start,
  handleSort,
  sort,
  onRowClick,
  paginationData,
  setPaginationData,
  customClassName,
  loading,
  stickyTableHeader = false,
}) => {
  const [pageSize, setPageSize] = useState(paginationData?.size || 10);
  const [isLoading, setIsLoading] = useState(true);
  const [isTableVisible, setIsTableVisible] = useState(true);

  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  // const [tableHeaderConfig, setTableHeaderConfig] = useState([]);
  // const [currentPage, setCurrentPage] = useState(paginationData.page || 1);
  const scrollRef = useDragScroll();
  const tableScrollRef = useRef(null);
  const scrollPos = useRef(0);
  const onPageChange = (page) => {
    setPaginationData((prev) => ({ ...prev, page: page }));
  };

  const hasGroupedHeader = headers?.some((item) => {
    // setTableHeaderConfig((prev) => [
    //   ...prev,
    //   item.children ? item.children : item,
    // ]);
    return item.children && item.children.length;
  });

  const tableHeaderConfig = headers?.flatMap((item) =>
    Array.isArray(item.children) ? item.children : item,
  );

  const checkScroll = () => {
    const el = tableScrollRef.current;
    if (!el) return;

    if (el.scrollLeft) {
      scrollPos.current = el.scrollLeft;
    }

    const { scrollLeft, scrollWidth, clientWidth } = el;

    const hasOverflow = scrollWidth > clientWidth + 5;

    if (!hasOverflow) {
      setShowLeft(false);
      setShowRight(false);
      return;
    }

    setShowLeft(scrollLeft > 5);
    setShowRight(scrollLeft + clientWidth < scrollWidth - 5);
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    checkScroll();

    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [tableScrollRef.current]);

  useEffect(() => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollLeft = scrollPos.current;
    }
  }, [tableData, tableScrollRef.current]);

  /* Important: recheck after data renders */
  useEffect(() => {
    const timer = setTimeout(() => {
      checkScroll();
    }, 150);

    return () => clearTimeout(timer);
  }, [tableData, headers]);

  const scroll = (direction) => {
    const el = tableScrollRef.current;
    if (!el) return;

    const scrollAmount = el.clientWidth * 0.8; // 320; //
    const newScrollPosition =
      direction === "left" ? -scrollAmount : scrollAmount;

    el.scrollBy({
      left: newScrollPosition,
      behavior: "smooth",
    });
    scrollPos.current = el.scrollLeft + newScrollPosition;
    setTimeout(checkScroll, 300);
  };

  useEffect(() => {
    if (showPagination && pageSize !== paginationData?.size) {
      setPaginationData((prev) => ({ ...prev, page: 1, size: pageSize }));
    }
  }, [pageSize]);

  useEffect(() => {
    // if (loading) {
    setIsLoading(loading);
    // }
  }, [loading]);

  useEffect(() => {
    if (!headers) return;
    const hasVisibleField = headers.some((item) => item.tableDisplay);
    if (!hasVisibleField) {
      setShowLeft(false);
      setShowRight(false);
    }
    setIsTableVisible(hasVisibleField);
  }, [headers]);

  return (
    <>
      {!isLoading ? (
        <div className="position-relative">
          {showPagination && (
            <div className="table-info-container align-item-center justify-content-between px-0">
              <div className="show-container d-flex flex-row align-items-center gap-2">
                <span>Show </span>
                <div>
                  {/* <div style={{ width: "90px" }}> */}
                  <FilterSelect
                    label=""
                    value={pageSize}
                    options={pageSizeOptions}
                    onChange={(item) => setPageSize(item.value)}
                    width="full"
                  />
                </div>
                <span> entries</span>
              </div>
              <div>
                <Pagination
                  onPageChange={onPageChange}
                  totalCount={totalRecords}
                  siblingCount={1}
                  currentPage={paginationData.page}
                  pageSize={pageSize}
                  className=""
                />
              </div>
            </div>
          )}
          {(showLeft || showRight) && (
            <span
              className={`slide-table-btn left ${!showLeft ? "disabled" : ""}`}
              onClick={() => scroll("left")}
              aria-label="Scroll left"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </span>
          )}
          {isTableVisible ? (
            <div
              className={`table-scroll-wrapper ${stickyTableHeader && "page-wrapper"}`}
            >
              <div
                // ref={scrollRef}
                ref={tableScrollRef}
                className="table-responsive drag-scroll"
                // style={{ maxWidth: "500px" }}
              >
                <Table className="tableBlock table-hover mb-0 ">
                  <thead className="text-uppercase">
                    {/* Row 1: Group Headers */}
                    {hasGroupedHeader && (
                      <tr className="group-header text-center">
                        {headers.map((item, index) => {
                          return item.children ? (
                            <th
                              colSpan={item.children.length}
                              className="text-nowrap"
                            >
                              {item.display}
                            </th>
                          ) : (
                            <th></th>
                          );
                        })}
                      </tr>
                    )}

                    {/* Row 2: Group Headers */}
                    <tr className="main-header align-middle" key={"No"}>
                      {tableHeaderConfig
                        ?.sort((a, b) => a?.tableRank - b?.tableRank)
                        ?.map(
                          (h, idx) =>
                            h.tableDisplay && (
                              <TableHeadItem
                                headingData={h}
                                dataKey={idx}
                                handleChange={handleSort}
                                sort={sort}
                                key={idx}
                                customClassName={customClassName}
                              />
                            ),
                        )}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData?.length > 0 ? (
                      tableData?.map((data, idx) => (
                        <tr className="align-middle" key={idx}>
                          {tableHeaderConfig?.map(
                            (h, idx) =>
                              h.tableDisplay && (
                                <TableItem
                                  rowData={data}
                                  headerDetails={h}
                                  headers={headers}
                                  dataKey={idx}
                                  key={idx}
                                  onRowClick={onRowClick}
                                  customClassName={customClassName}
                                />
                              ),
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <DataNotFound />
                        </td>
                      </tr>
                    )}
                    {/* <span
                    className={`slide-btn right`}
                    // onClick={() => scroll("right")}
                    aria-label="Scroll right"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </span> */}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="text-center m-5">
              No fields selected. Please select at least one field to display
              the data.
            </div>
          )}
          {(showLeft || showRight) && (
            <span
              className={`slide-table-btn right ${!showRight ? "disabled" : ""}`}
              onClick={() => scroll("right")}
              aria-label="Scroll right"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </span>
          )}
          {showPagination && (
            <div className="table-info-container align-item-center justify-content-between px-0 pt-4">
              <div className="show-container">
                {" "}
                {`Showing ${((paginationData.page - 1) * paginationData.size + 1).toLocaleString("en-US")} to ${Math.min(
                  totalRecords,
                  paginationData.page * paginationData.size,
                ).toLocaleString(
                  "en-IN",
                )} of ${totalRecords.toLocaleString("en-US")} entries`}
              </div>
              <div>
                <Pagination
                  onPageChange={onPageChange}
                  totalCount={totalRecords}
                  siblingCount={1}
                  currentPage={paginationData.page}
                  pageSize={pageSize}
                  className=""
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <Loader />
      )}
    </>
  );
};

const TableDetails = (props) => (
  <ErrorBoundary>
    <TableDetailsInner {...props} />
  </ErrorBoundary>
);

export default TableDetails;
